import {
  Box,
  CloseButton,
  Dialog,
  HStack,
  Portal,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import React from 'react';
import { TrackingType } from '@/types';
import { LabelComposer } from './LabelComposer';

export interface ComposerLaunch {
  /** Existing tracked identities to preselect. */
  codes?: string[];
  /** ERP sources; canonical sources are registered only when the user prepares labels. */
  sources?: TrackingType.ComposerSource[];
  /** Calling page context, recorded with the job (never changes the caller's selection). */
  context?: {
    page: string;
    project_id?: number | null;
    release_id?: number | null;
    return_to?: string;
  };
  mode?: 'labels' | 'reports';
  title?: string;
}

interface ComposerContextValue {
  open: (launch: ComposerLaunch) => void;
  close: () => void;
  isOpen: boolean;
}

const ComposerContext = React.createContext<ComposerContextValue | null>(null);

export const LabelComposerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [launch, setLaunch] = React.useState<ComposerLaunch | null>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const fullScreen = useBreakpointValue({ base: true, md: false }) ?? false;

  const open = React.useCallback((next: ComposerLaunch) => {
    triggerRef.current = (document.activeElement as HTMLElement) ?? null;
    setLaunch({ ...next, mode: next.mode ?? 'labels' });
  }, []);

  const close = React.useCallback(() => {
    setLaunch(null);
    const trigger = triggerRef.current;
    window.setTimeout(() => trigger?.focus?.(), 0);
  }, []);

  const value = React.useMemo(
    () => ({ open, close, isOpen: Boolean(launch) }),
    [open, close, launch],
  );

  return (
    <ComposerContext.Provider value={value}>
      {children}
      <Dialog.Root
        open={Boolean(launch)}
        onOpenChange={(e) => {
          if (!e.open) close();
        }}
        size={fullScreen ? 'full' : 'cover'}
        scrollBehavior="inside"
        closeOnInteractOutside={false}
        restoreFocus={false}
        trapFocus
      >
        <Portal>
          <Dialog.Backdrop style={{ zIndex: 100000 }} />
          <Dialog.Positioner style={{ zIndex: 100001 }}>
            <Dialog.Content
              maxW={fullScreen ? undefined : '1120px'}
              maxH={fullScreen ? '100dvh' : 'calc(100dvh - 2rem)'}
              display="flex"
              flexDirection="column"
              my={fullScreen ? 0 : 4}
            >
              <Dialog.Header borderBottomWidth="1px" py={3} flexShrink={0}>
                <HStack justify="space-between" w="100%">
                  <Box minW={0}>
                    <Dialog.Title>
                      {launch?.mode === 'reports'
                        ? 'Print latest reports'
                        : 'Print tracking labels'}
                    </Dialog.Title>
                    {launch?.title && (
                      <Text textStyle="body.small" color="fg.muted" truncate>
                        {launch.title}
                      </Text>
                    )}
                  </Box>
                  <Dialog.CloseTrigger asChild position="static">
                    <CloseButton size="sm" aria-label="Close" />
                  </Dialog.CloseTrigger>
                </HStack>
              </Dialog.Header>
              <Dialog.Body
                p={0}
                flex="1"
                minH={0}
                overflow="hidden"
                display="flex"
              >
                {launch && (
                  <LabelComposer
                    launch={launch}
                    variant="dialog"
                    onClose={close}
                  />
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </ComposerContext.Provider>
  );
};

export const useLabelComposer = () => {
  const ctx = React.useContext(ComposerContext);
  if (!ctx)
    throw new Error(
      'useLabelComposer must be used inside LabelComposerProvider',
    );
  return ctx;
};
